import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { getMessagesHubUrl } from "@/config/signalr";
const HUB_EVENT_NEW_MESSAGE = "NewMessage";
const HUB_METHOD_JOIN_THREAD = "JoinThread";
/** True when the error is from our own stop() during negotiation (e.g. unmount/Strict Mode). */
function isStoppedDuringNegotiation(err) {
    const msg = err?.message ?? "";
    return msg.includes("stopped during negotiation");
}
/**
 * Connects to the messages SignalR hub and subscribes to NewMessage for the
 * given thread. Use when viewing a quote (or job) message thread so new
 * messages appear in real time.
 */
export function useMessagesSignalR(threadId, options = {}) {
    const { onNewMessage, onConnectionError } = options;
    const onNewMessageRef = useRef(onNewMessage);
    const onErrorRef = useRef(onConnectionError);
    onNewMessageRef.current = onNewMessage;
    onErrorRef.current = onConnectionError;
    useEffect(() => {
        if (!threadId)
            return;
        const url = getMessagesHubUrl();
        const token = localStorage.getItem("accessToken");
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
            accessTokenFactory: () => token ?? "",
            withCredentials: false,
        })
            .withAutomaticReconnect()
            .build();
        connection.on(HUB_EVENT_NEW_MESSAGE, (payload) => {
            if (payload.threadId === threadId && onNewMessageRef.current) {
                onNewMessageRef.current(payload);
            }
        });
        let cancelled = false;
        connection.onclose((error) => {
            if (!error || cancelled || isStoppedDuringNegotiation(error))
                return;
            if (onErrorRef.current)
                onErrorRef.current(error);
        });
        connection
            .start()
            .then(() => {
            if (cancelled)
                return;
            return connection.invoke(HUB_METHOD_JOIN_THREAD, threadId);
        })
            .catch((err) => {
            if (cancelled || isStoppedDuringNegotiation(err))
                return;
            if (onErrorRef.current)
                onErrorRef.current(err);
        });
        return () => {
            cancelled = true;
            connection.stop().catch(() => { });
        };
    }, [threadId]);
}
